import { Button, LoadingModal } from "@mixitone/components";
import { Account, acceptanceSourceLabel, documentRequiredBeforeMembership } from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import { formatDate } from "@mixitone/util";
import AppModal from "components/common/AppModal";
import { injectModal } from "lib/injectParams";
import { DocumentAcceptancesController } from "./DocumentAcceptancesController";

interface Props {
  clubId: string;
  documentName: string;
}

const DocumentAcceptancesModal: React.FC<Props> = ({ documentName }) => {
  const controller = DocumentAcceptancesController.use();
  const { loading, latestDocument, members, recordingPlayerId } = controller.state;
  const acceptanceRequired = latestDocument ? documentRequiredBeforeMembership(latestDocument) : false;

  if (!Account.current?.hasFeature("member_players")) {
    return (
      <AppModal title="Access Denied">
        <div className="p-4 text-center text-gray-500">
          To access documents, please{" "}
          <a href="/settings/account/billing" className="text-blue-500 hover:underline">
            upgrade to a Pro account
          </a>
          .
        </div>
      </AppModal>
    );
  }

  if (loading) return <LoadingModal title="Acceptances" />;

  return (
    <AppModal title={`${documentName} acceptances`} className="max-h-[80vh]">
      <p className="mb-4 text-sm text-gray-600">
        {latestDocument
          ? acceptanceRequired
            ? `Version ${latestDocument.version} requires acceptance. Recording it here counts as the member agreeing.`
            : "Acceptance is not required for this document. Earlier agreements are listed below."
          : "This document has no current version."}
      </p>
      {members.length === 0 ? (
        <p className="text-sm text-gray-600">No current members.</p>
      ) : (
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-gray-500">
                <th className="px-2 py-2">Member</th>
                <th className="px-2 py-2">Version</th>
                <th className="px-2 py-2">When</th>
                <th className="px-2 py-2">Recorded as</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {members.map((member) => {
                const acceptance = controller.acceptanceFor(member);
                const covered = latestDocument != null && member.hasAcceptedDocumentVersion(latestDocument);
                return (
                  <tr key={member.id}>
                    <td className="px-2 py-2">{member.name}</td>
                    <td className="px-2 py-2">
                      {acceptance ? `v${acceptance.version}${covered ? "" : " (older)"}` : "Not accepted"}
                    </td>
                    <td className="px-2 py-2">{acceptance ? formatDate(acceptance.accepted_at) : ""}</td>
                    <td className="px-2 py-2">
                      {acceptance ? acceptanceSourceLabel(acceptance.source) : ""}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {controller.canRecord(member) && (
                        <Button
                          kind="secondary"
                          disabled={recordingPlayerId === member.id}
                          spinner={recordingPlayerId === member.id}
                          onClick={() => controller.actionRecordAcceptance(member)}
                        >
                          Record acceptance
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AppModal>
  );
};

export default injectModal(DocumentAcceptancesController.scope(ApplicationView(DocumentAcceptancesModal)));
